import React, { useState } from 'react';
import { Calendar } from '../components/ui/calendar';
import { format } from 'date-fns';
import { Clock, CheckCircle, User, Mail, Phone, MessageSquare, ArrowLeft, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const SchedulingPage = () => {
  const [step, setStep] = useState(1);
  const [selectedService, setSelectedService] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const services = [
    { id: 'consultation', name: 'Free Consultation', duration: '30 min' },
    { id: 'personal', name: 'Personal Training Session', duration: '60 min' },
    { id: 'assessment', name: 'Fitness Assessment', duration: '45 min' },
    { id: 'nutrition', name: 'Nutrition Consultation', duration: '45 min' }
  ];

  // Mock available time slots
  const timeSlots = [
    '6:00 AM', '7:00 AM', '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM',
    '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM',
    '6:00 PM', '7:00 PM'
  ];

  const handleServiceSelect = (serviceId) => {
    setSelectedService(serviceId);
    setStep(2);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setStep(3);
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    setStep(4);
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock booking submission
    toast.success('Booking request sent! Vincent will confirm your appointment within 24 hours.');
    
    // Reset form
    setTimeout(() => {
      setStep(5);
    }, 1000);
  };

  const handleReset = () => {
    setStep(1);
    setSelectedService('');
    setSelectedDate(null);
    setSelectedTime('');
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  const goBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const getServiceName = (id) => {
    const service = services.find(s => s.id === id);
    return service ? service.name : '';
  };

  return (
    <div className="scheduling-page">
      <div className="scheduling-hero">
        <div className="container">
          <h1 className="scheduling-title">Schedule Your Session</h1>
          <p className="scheduling-subtitle">
            Book your free consultation or training session with Vincent
          </p>
        </div>
      </div>

      <div className="container scheduling-container">
        {/* Progress Steps */}
        <div className="progress-steps">
          <div className={`progress-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
            <div className="step-circle">1</div>
            <span className="step-label">Service</span>
          </div>
          <div className="progress-line"></div>
          <div className={`progress-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
            <div className="step-circle">2</div>
            <span className="step-label">Date</span>
          </div>
          <div className="progress-line"></div>
          <div className={`progress-step ${step >= 3 ? 'active' : ''} ${step > 3 ? 'completed' : ''}`}>
            <div className="step-circle">3</div>
            <span className="step-label">Time</span>
          </div>
          <div className="progress-line"></div>
          <div className={`progress-step ${step >= 4 ? 'active' : ''} ${step > 4 ? 'completed' : ''}`}>
            <div className="step-circle">4</div>
            <span className="step-label">Details</span>
          </div>
        </div>

        {/* Step Content */}
        <div className="scheduling-content">
          {/* Step 1: Select Service */}
          {step === 1 && (
            <div className="step-content">
              <h2 className="step-title">Select a Service</h2>
              <p className="step-description">Choose the type of session you'd like to book</p>
              
              <div className="services-grid">
                {services.map((service) => (
                  <div
                    key={service.id}
                    className="service-option"
                    onClick={() => handleServiceSelect(service.id)}
                  >
                    <h3>{service.name}</h3>
                    <p className="service-duration">
                      <Clock size={16} />
                      {service.duration}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Select Date */}
          {step === 2 && (
            <div className="step-content">
              <button className="back-button" onClick={goBack}>
                <ArrowLeft size={20} />
                Back
              </button>
              
              <h2 className="step-title">Select a Date</h2>
              <p className="step-description">
                Choose your preferred date for {getServiceName(selectedService)}
              </p>
              
              <div className="calendar-wrapper">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  disabled={(date) => date < new Date() || date.getDay() === 0}
                  className="scheduling-calendar"
                />
              </div>
              
              <p className="calendar-note">* Sundays are unavailable</p>
            </div>
          )}

          {/* Step 3: Select Time */}
          {step === 3 && (
            <div className="step-content">
              <button className="back-button" onClick={goBack}>
                <ArrowLeft size={20} />
                Back
              </button>
              
              <h2 className="step-title">Select a Time</h2>
              <p className="step-description">
                Available times for {selectedDate && format(selectedDate, 'MMMM d, yyyy')}
              </p>
              
              <div className="time-slots-grid">
                {timeSlots.map((time) => (
                  <button
                    key={time}
                    className="time-slot"
                    onClick={() => handleTimeSelect(time)}
                  >
                    <Clock size={18} />
                    {time}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Contact Details */}
          {step === 4 && (
            <div className="step-content">
              <button className="back-button" onClick={goBack}>
                <ArrowLeft size={20} />
                Back
              </button>
              
              <h2 className="step-title">Your Details</h2>
              <p className="step-description">
                Complete your booking information
              </p>

              <div className="booking-summary">
                <h3>Booking Summary</h3>
                <div className="summary-item">
                  <strong>Service:</strong> {getServiceName(selectedService)}
                </div>
                <div className="summary-item">
                  <strong>Date:</strong> {selectedDate && format(selectedDate, 'MMMM d, yyyy')}
                </div>
                <div className="summary-item">
                  <strong>Time:</strong> {selectedTime}
                </div>
              </div>

              <form onSubmit={handleSubmit} className="booking-form">
                <div className="form-group">
                  <label htmlFor="name">
                    <User size={18} />
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    required
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    <Mail size={18} />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    required
                    placeholder="your.email@example.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">
                    <Phone size={18} />
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleFormChange}
                    required
                    placeholder="(239) 555-0123"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message">
                    <MessageSquare size={18} />
                    Message (Optional)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleFormChange}
                    rows="4"
                    placeholder="Any specific goals or questions?"
                  />
                </div>

                <button type="submit" className="btn-cta">
                  Confirm Booking
                  <ArrowRight size={20} style={{ marginLeft: '8px' }} />
                </button>
              </form>
            </div>
          )}

          {/* Step 5: Confirmation */}
          {step === 5 && (
            <div className="step-content confirmation-step">
              <div className="confirmation-icon">
                <CheckCircle size={64} />
              </div>
              <h2 className="confirmation-title">Booking Request Sent!</h2>
              <p className="confirmation-message">
                Thank you for booking with V-Fit! Vincent will review your request and send you a 
                confirmation email within 24 hours.
              </p>
              
              <div className="confirmation-details">
                <h3>Your Booking Details:</h3>
                <div className="detail-item">
                  <strong>Service:</strong> {getServiceName(selectedService)}
                </div>
                <div className="detail-item">
                  <strong>Date:</strong> {selectedDate && format(selectedDate, 'MMMM d, yyyy')}
                </div>
                <div className="detail-item">
                  <strong>Time:</strong> {selectedTime}
                </div>
                <div className="detail-item">
                  <strong>Name:</strong> {formData.name}
                </div>
                <div className="detail-item">
                  <strong>Email:</strong> {formData.email}
                </div>
              </div>

              <div className="confirmation-actions">
                <button onClick={handleReset} className="btn-primary">
                  Book Another Session
                </button>
                <a href="/" className="btn-secondary">
                  Return to Home
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SchedulingPage;
